import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import UnifiedMarketsNotificationsProvider from '@paradigm/features/src/unified-rfqs/ui/notifications/notifications-context';
import { getUserID, selectIsAuthenticated } from '@paradigm/store/auth';
import { TabsState } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/tabs-context';

import { playNotificationSound, fireNotification } from '#/notifications';
import { UNIFIED_RFQS_ROUTE } from '#/routes';
import { usePinnedRfqsAction } from '@paradigm/features/src/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import localStorage from '@paradigm/utils/src/safeLocalStorage';
import { logException } from '@paradigm/logging';
import {
  selectStoredMainTabFilter,
  tabFiltersStorageKey,
} from '#/components/UnifiedRfqs/tab-filters-storage';

interface Props {
  children: JSX.Element;
}

export default function UnifiedRfqsNotificationsProvider({
  children,
}: Props): JSX.Element {
  const pathname = useSelector((state) => state.router.location.pathname);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userId = useSelector(getUserID);
  const { pinRfq } = usePinnedRfqsAction();
  const isViewingUmPage = pathname.includes(UNIFIED_RFQS_ROUTE);

  // Read on demand rather than up front: the page rewrites the stored filter
  // as the user edits it, and it is the tab the page would reopen on that
  // decides once the page has no live filter to offer.
  const getStoredRfqFilter = useCallback(
    () => readStoredMainTabFilter(userId),
    [userId],
  );

  if (!isAuthenticated) return children;

  return (
    <UnifiedMarketsNotificationsProvider
      playNotificationSound={playNotificationSound}
      fireNotification={fireNotification}
      pinRfq={pinRfq}
      isViewingUmPage={isViewingUmPage}
      getStoredRfqFilter={getStoredRfqFilter}
    >
      {children}
    </UnifiedMarketsNotificationsProvider>
  );
}

function readStoredMainTabFilter(userId: string | number) {
  try {
    const stored = localStorage.getItem(tabFiltersStorageKey(userId));
    if (stored == null) return null;

    return selectStoredMainTabFilter(JSON.parse(stored) as TabsState);
  } catch (error) {
    logException('Failed reading the stored unified markets filter', error);
    return null;
  }
}
